import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/aplicativos-iptv/lazer-play")({
  head: () => ({
    meta: [
      { title: "Como Baixar e Instalar o Lazer Play na Samsung, LG e Roku TV | P2 PRO" },
      { name: "description", content: "Aprenda como baixar e instalar o aplicativo Lazer Play na sua Smart TV Samsung, LG ou Roku TV. Guia completo passo a passo atualizado em 2026." },
    ],
  }),
  component: () => (
    <SEOPage
      category="Tutorial"
      title="Como Baixar e Instalar o Lazer Play na Samsung, LG e Roku TV"
      subtitle="Lazer Play: Aplicativo Compatível com Painel IPTV Pós Pago"
      introParagraphs={[
        "O Lazer Play é um aplicativo desenvolvido para oferecer praticidade, navegação intuitiva e excelente compatibilidade com Smart TVs. Disponível para Samsung, LG e Roku TV, o aplicativo permite uma experiência moderna e otimizada para quem busca facilidade de acesso e bom desempenho.",
        "Neste guia você aprenderá como baixar e instalar o Lazer Play passo a passo em sua Smart TV."
      ]}
      sections={[
        {
          title: "O Que é o Lazer Play?",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                O Lazer Play é um aplicativo compatível com televisores inteligentes que oferece uma interface moderna e simples de utilizar. Seu objetivo é proporcionar uma experiência rápida e organizada para os usuários.
              </p>
              <p>
                Além disso, o aplicativo possui compatibilidade com sistemas profissionais de Painel IPTV Pós Pago, sendo uma das opções utilizadas por revendedores que procuram soluções estáveis para seus clientes.
              </p>
            </div>
          )
        },
        {
          title: "Como Instalar o Lazer Play na Samsung Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Conecte sua Samsung Smart TV à internet." },
                  { step: 2, text: "Abra a loja de aplicativos da Samsung." },
                  { step: 3, text: "Utilize a ferramenta de pesquisa." },
                  { step: 4, text: "Digite: Lazer Play" },
                  { step: 5, text: "Selecione o aplicativo oficial." },
                  { step: 6, text: "Clique em Instalar." },
                  { step: 7, text: "Aguarde a conclusão da instalação." },
                  { step: 8, text: "Abra o aplicativo e informe seus dados de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! O Lazer Play estará disponível em sua Samsung Smart TV.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o Lazer Play na LG Smart TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Certifique-se de que sua LG Smart TV está conectada à internet." },
                  { step: 2, text: "Abra a LG Content Store." },
                  { step: 3, text: "Acesse a área de pesquisa." },
                  { step: 4, text: "Pesquise por: Lazer Play" },
                  { step: 5, text: "Selecione o aplicativo." },
                  { step: 6, text: "Clique em Instalar." },
                  { step: 7, text: "Aguarde a instalação ser concluída." },
                  { step: 8, text: "Abra o aplicativo e faça login." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Agora o Lazer Play está pronto para uso em sua LG Smart TV.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o Lazer Play na Roku TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground italic">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Acesse a tela inicial da Roku TV." },
                  { step: 2, text: "Entre na opção: Streaming Channels" },
                  { step: 3, text: "Utilize a busca da Roku Store." },
                  { step: 4, text: "Digite: Lazer Play" },
                  { step: 5, text: "Selecione o aplicativo oficial." },
                  { step: 6, text: "Clique em: Adicionar Canal" },
                  { step: 7, text: "Aguarde a instalação." },
                  { step: 8, text: "Abra o aplicativo e informe suas credenciais de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-electric/30 transition-colors">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-black uppercase tracking-widest text-sm">Pronto! O Lazer Play estará disponível para utilização em sua Roku TV.</p>
            </div>
          )
        },
        {
          title: "Dispositivos Compatíveis com o Lazer Play",
          type: "featured",
          content: (
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Samsung Smart TV",
                "LG Smart TV",
                "Roku TV",
                "Dispositivos compatíveis com as lojas oficiais do aplicativo"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/5">
                  <CheckCircle2 className="w-5 h-5 text-electric shrink-0" />
                  <span className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Principais Benefícios do Lazer Play",
          type: "default",
          content: (
            <div className="grid sm:grid-cols-2 gap-4 text-muted-foreground">
              {[
                "Interface moderna",
                "Navegação intuitiva",
                "Fácil instalação",
                "Compatibilidade com Smart TVs",
                "Excelente desempenho",
                "Atualizações frequentes",
                "Integração com Painel IPTV Pós Pago"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Lazer Play Compatível com Painel IPTV Pós Pago",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed text-lg italic">
              <p>
                O Lazer Play funciona perfeitamente com sistemas modernos de Painel IPTV Pós Pago, permitindo uma experiência organizada e eficiente para usuários e revendedores.
              </p>
              <p>
                Sua compatibilidade com Smart TVs facilita a utilização em diferentes ambientes, tornando-se uma excelente alternativa para quem busca praticidade e estabilidade.
              </p>
            </div>
          )
        },
        {
          title: "Aplicativos Compatíveis com Nossa Estrutura",
          type: "default",
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">Além do Lazer Play, trabalhamos com outros aplicativos amplamente utilizados:</p>
              <ul className="grid gap-3 list-none p-0">
                {["One X", "Fun Play", "P2PRO", "P2PLAY", "Prime Play"].map((item) => (
                  <li key={item} className="flex items-center gap-3 m-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mt-4">Todos compatíveis com nosso sistema profissional de Painel IPTV Pós Pago.</p>
            </div>
          )
        },
        {
          title: "Não Encontrou o Lazer Play?",
          type: "default",
          content: (
            <div className="grid gap-4">
              {[
                "Verifique a conexão com a internet.",
                "Atualize o sistema da TV.",
                "Reinicie o aparelho.",
                "Atualize a loja de aplicativos.",
                "Entre em contato com o suporte."
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-red-500/5 border border-red-500/10">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-muted-foreground font-medium">{item}</span>
                </div>
              ))}
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Interface Moderna", description: "Design limpo para facilitar a navegação." },
        { title: "Fácil Instalação", description: "Configurado em minutos diretamente na loja." },
        { title: "Estabilidade", description: "Streaming otimizado para evitar travamentos." }
      ]}
      howItWorks={[
        { title: "Busque", description: "Acesse a loja oficial da sua Smart TV." },
        { title: "Instale", description: "Siga o passo a passo do tutorial acima." },
        { title: "Acesse", description: "Use seus dados do Painel Pós Pago." },
        { title: "Assista", description: "Aproveite a melhor experiência IPTV." }
      ]}
      diferenciais={[
        { title: "Nativo Smart TV", description: "Otimizado para Samsung, LG e Roku TV." },
        { title: "Pós Pago", description: "Totalmente integrado à nossa estrutura profissional." },
        { title: "Qualidade", description: "Aproveite a máxima definição da sua TV." }
      ]}
      faq={[
        { question: "O Lazer Play funciona na Samsung Smart TV?", answer: "Sim. O aplicativo possui compatibilidade com modelos Samsung compatíveis." },
        { question: "O Lazer Play funciona na LG Smart TV?", answer: "Sim. O aplicativo está disponível para televisores LG compatíveis." },
        { question: "O Lazer Play funciona na Roku TV?", answer: "Sim. O aplicativo pode ser instalado diretamente pela Roku Store." },
        { question: "Preciso de login para utilizar o Lazer Play?", answer: "Sim. Após a instalação, basta inserir os dados de acesso fornecidos pelo seu revendedor." },
        { question: "O Lazer Play é compatível com Painel IPTV Pós Pago?", answer: "Sim. O aplicativo é compatível com sistemas profissionais de Painel IPTV Pós Pago." }
      ]}
      breadcrumbs={[
        { label: "Aplicativos", href: "/aplicativos-iptv" },
        { label: "Lazer Play", href: "/aplicativos-iptv/lazer-play" }
      ]}
      ctaTitle="🚀 Tenha Seu Próprio Painel IPTV Pós Pago"
      ctaDescription="Comece hoje mesmo com uma estrutura profissional pronta para revenda. One X, Lazer Play, Fun Play, P2PRO, P2PLAY e Prime Play."
      ctaButtonText="QUERO TESTAR O PAINEL IPTV PÓS PAGO AGORA"
    />
  ),
});